import i18n from 'i18next';
import { NotificationContent, Notifications } from '../../../Notifications';
import { ItemsGroupByType } from '../../../../core/models/organization/settings/enum';
import { ToastWithSubHeader } from '../../../../components/toasts/ToastWithSubheader';
import { useState } from 'react';
import { useStores } from '../../../../hooks';
import { CombinedItem } from '../../../../core/models';
import { ItemAddToPracticesParams } from '../../components/ItemAddPractice/ItemAddPractices';
import { SiteEntityInfo } from '../../../practiceConnections/MyOrganizationPageStore';
import { BulkUpdateCombinedItemType } from '../../../../core/models/datasource/combinedItems/parts/bulkUpdateCombinedItemQueryUpdate';

interface AddToPracticesParams {
    practicesWithoutItems?: string[];
    itemsKeys?: Set<string>;
}

export const useCombinedItemBulkAction = () => {
    const {
        domain: { combinedItemStore, constantsStore },
    } = useStores();

    const { organizationSettings, applications } = constantsStore;
    const { items = [], selectedItems = [] } = combinedItemStore;
    const [addToPracticesParams = {}, setAddToPracticesParams] = useState<AddToPracticesParams>();

    const getAddItemToPracticesMessage = (params: { practiceCount: number; isScheduledUpdate: boolean }): NotificationContent => {
        const { practiceCount, isScheduledUpdate } = params;
        const message = isScheduledUpdate
            ? i18n.t('controlCenter:addCombinedItemsToPracticesSuccessfullyScheduled', {
                  count: practiceCount,
                  defaultValue: `Add Combi Products to ${practiceCount} Practice(s) Successfully Scheduled`,
              })
            : i18n.t('controlCenter:addingCombinedItemsToAdditionalPractices', {
                  count: practiceCount,
                  defaultValue: `Adding Combi Products to ${practiceCount} Additional Practice(s)`,
              });

        const subMessage = i18n.t('controlCenter:viewUpdateStatusInActivityLog', {
            defaultValue: `View the update's status in the activity log.`,
        });

        return ToastWithSubHeader({ message, subMessage });
    };

    const getItemKey = (item: CombinedItem) =>
        (organizationSettings?.itemsGroupBy === ItemsGroupByType.Description && item.description) || item.pimsGeneratedId;

    const onAddToPracticesAction = () => {
        const itemsKeys = new Set<string>();
        const groupedItems = new Map<string, CombinedItem[]>();

        selectedItems.forEach((item) => {
            const group = groupedItems.get(item.provider.siteId) || [];
            groupedItems.set(item.provider.siteId, [...group, item]);
            itemsKeys.add(getItemKey(item));
        });

        const practicesWithoutItems = applications.reduce((agg, { id: siteId }) => {
            if ((groupedItems.get(siteId)?.length || 0) < itemsKeys.size) {
                return [...agg, siteId];
            }
            return agg;
        }, [] as string[]);

        if (applications.length === practicesWithoutItems.length) {
            const message = i18n.t(
                'controlCenter:allInvoiceItemsCouldNotBeFoundInClinic',
                'All Combi Products could not be found in at least one clinic',
            );
            Notifications.error(message);
            setAddToPracticesParams(undefined);
            return;
        }

        if (!practicesWithoutItems.length) {
            const message = i18n.t('controlCenter:invoiceItemsExistInAllAvailableClinics', 'Combi Products exist in all available clinics');
            Notifications.error(message);
            setAddToPracticesParams(undefined);
            return;
        }

        setAddToPracticesParams({ practicesWithoutItems, itemsKeys: itemsKeys });
    };

    const onAddToPracticesSubmit = async (params: ItemAddToPracticesParams): Promise<Partial<CombinedItem>[] | undefined> => {
        const { originPractice, targetPractices, runAt, name } = params;
        const keyGroupedItems = items.reduce((agg, item) => {
            const itemKey = getItemKey(item);
            const group = agg.get(itemKey) || [];
            return agg.set(itemKey, [...group, item]);
        }, new Map<string, CombinedItem[]>());

        const { createItems, practices } = [...keyGroupedItems.keys()].reduce(
            (agg, itemKey) => {
                if (!addToPracticesParams?.itemsKeys?.has(itemKey)) {
                    return agg;
                }

                const masterItem: CombinedItem | undefined = keyGroupedItems
                    .get(itemKey)
                    ?.find(({ provider }) => provider.siteId === originPractice.id);
                if (!masterItem) {
                    return agg;
                }

                const existInPractices = keyGroupedItems.get(itemKey)?.map(({ provider }) => provider.siteId) || [];
                const practicesToCreateIn = targetPractices.filter(({ id }) => !existInPractices.includes(id));
                const newItems = practicesToCreateIn.map(
                    (practice) =>
                        ({
                            provider: (practice as SiteEntityInfo).provider,
                            pimsId: masterItem.pimsId,
                            description: masterItem.description,
                            descriptionOnInvoice: masterItem.descriptionOnInvoice,

                            hideZeroAmounts: masterItem.hideZeroAmounts,
                            checked: masterItem.checked,
                            masterSiteId: masterItem.provider.siteId,
                        } as Partial<CombinedItem>),
                );

                practicesToCreateIn.forEach(({ id }) => agg.practices.add(id));

                return { ...agg, createItems: [...agg.createItems, ...newItems] };
            },
            { createItems: [] as Partial<CombinedItem>[], practices: new Set<string>() },
        );

        if (!createItems.length) {
            Notifications.error('Combi Products exist in all selected clinics');
            return;
        }

        await combinedItemStore.saveItems({
            items: createItems,
            type: BulkUpdateCombinedItemType.CREATE,
            runAt,
            name,
            message: getAddItemToPracticesMessage({ practiceCount: practices.size, isScheduledUpdate: Boolean(runAt) }),
        });
        setAddToPracticesParams(undefined);

        return createItems;
    };

    const onAddToPracticesCancel = () => {
        setAddToPracticesParams(undefined);
    };

    return {
        addToPractices: {
            ...addToPracticesParams,
            action: onAddToPracticesAction,
            submit: onAddToPracticesSubmit,
            cancel: onAddToPracticesCancel,
        },
    };
};
